/**
 * The understanding models, pinned by revision (transformers.js downloads them from the Hugging
 * Face hub on first use and keeps them in the browser's Cache API). Chosen by the evals in
 * stt.eval.ts and understand.eval.ts (data/experiments/stt, data/experiments/slm; summary in
 * docs/design/voice-understanding.md).
 */
export interface PinnedModel {
  repo: string;
  revision: string;
  dtype: 'q8' | 'int8' | 'q4' | 'fp32';
  /** Download size (MB, measured from the hub listing). */
  mb: number;
  licence: string;
  /**
   * Good enough to use: the untuned language models pick topics no better than chance
   * (understand.eval.ts, 10-15 % against 79 % for the keywords), so the language model stays off
   * until it is fine-tuned.
   */
  use: boolean;
}

/** Speech-to-text: short answers and questions from the child. */
export const STT: PinnedModel = {
  repo: 'onnx-community/moonshine-base-ONNX',
  revision: 'b1e9b6aae3c3c7298f10c3798393fdf38e8fbbad',
  dtype: 'q8',
  mb: 63,
  licence: 'MIT',
  use: true,
};

/**
 * Which approved topic a child's question is about: a cross-encoder (nli-deberta-v3-xsmall,
 * fine-tuned by ml/experiments/train_lab_xenc.py, 8-bit ONNX, 87 MB + 8 MB tokenizer). On the
 * independent test the rules then this model (p >= 0.95) answered 90.8 % of questions right
 * against 75.9 % for the rules alone, with no more wrong answers (2.3 %). For answer choices it
 * added wrong picks, so it is used for questions only. Hosted on Cloudflare R2 (too large for
 * Pages): `uv run foe-ml hosting publish`, whose manifest ml/hosting/topic-xenc.json pins the
 * version below (models.test.ts checks they match).
 */
export const TOPIC_MODEL = {
  host: 'https://r2.makersmark.app/',
  /** `<name>/<version>` under models/ on the host. */
  path: 'topic-xenc/cbc81bbcff6b',
  dtype: 'q8' as const,
  /** Its pick counts at or above this probability. */
  trust: 0.95,
  use: true,
};

/** The small language model that sorts questions into the approved topics. */
export const SLM: PinnedModel = {
  repo: 'HuggingFaceTB/SmolLM2-360M-Instruct',
  revision: 'a10cc1512eabd3dde888204e902eca88bddb4951',
  dtype: 'int8',
  mb: 365,
  licence: 'Apache-2.0',
  use: false,
};
